/**
 * Predicted-pain-by-cycle-day chart, shared by the dashboard CycleCard and the
 * Pain Patterns page. Each bar is the predicted pain (0–10) for that cycle day,
 * colored by predicted phase; the translucent band behind it is the min–max
 * range from the user's history.
 */

import type { useCyclePredictor } from '@pelvia/shared/hooks/patient/useCyclePredictor'

export const PHASE_COLOR: Record<string, string> = {
  period: '#c94040',
  follicular: '#e8a44a',
  ovulation: '#4a8c5c',
  luteal: '#6b5ea8',
}

export const DEFAULT_PHASE_LEGEND = [
  { name: 'Period', color: '#c94040' },
  { name: 'Follicular', color: '#e8a44a' },
  { name: 'Ovulation', color: '#4a8c5c' },
  { name: 'Luteal', color: '#6b5ea8' },
]

export function shortMonthDay(iso: string): string {
  const [, mo, day] = iso.split('-')
  if (!mo || !day) return iso
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${months[Number.parseInt(mo, 10) - 1]} ${Number.parseInt(day, 10)}`
}

type Chart = NonNullable<ReturnType<typeof useCyclePredictor>['data']>['chart']

export function CycleChart({ chart }: { chart: Chart }) {
  if (!chart) return null
  const CHART_H = 120
  const todayIdx = chart.findIndex((d) => d.is_today)
  const lastIdx = chart.length - 1
  return (
    <figure className="cycle-chart-figure">
      <figcaption className="cycle-chart-caption">
        Predicted pain by day — taller bar means more pain expected. Shaded band
        is your usual range.
      </figcaption>
      <div className="cycle-chart-wrap">
        <div className="cycle-y-axis" style={{ height: CHART_H }}>
          <span>10</span>
          <span>5</span>
          <span>0</span>
        </div>
        <div className="cycle-chart-col">
          <div className="cycle-chart" style={{ height: CHART_H }}>
            {chart.map((d) => {
              const color = PHASE_COLOR[d.phase] ?? 'var(--accent)'
              const avgPx = (d.predicted_pain / 10) * CHART_H
              const minPx = (d.pain_min / 10) * CHART_H
              const maxPx = (d.pain_max / 10) * CHART_H
              return (
                <div
                  key={d.date}
                  className={`cycle-bar-col${d.is_today ? ' cycle-bar-today' : ''}`}
                  title={`Day ${d.cycle_day}: predicted pain ${d.predicted_pain}/10 (${d.phase})`}
                >
                  <div
                    className="cycle-bar-range"
                    style={{
                      bottom: `${minPx}px`,
                      height: `${Math.max(0, maxPx - minPx)}px`,
                      background: `${color}25`,
                    }}
                  />
                  <div
                    className="cycle-bar-fill"
                    style={{ height: `${avgPx}px`, background: color }}
                  />
                </div>
              )
            })}
          </div>
          <div className="cycle-x-axis" aria-hidden="true">
            {chart.map((d, i) => {
              const showLabel =
                i === 0 ||
                i === lastIdx ||
                i === todayIdx ||
                (todayIdx >= 0
                  ? Math.abs(i - todayIdx) % 7 === 0
                  : i % 7 === 0)
              return (
                <span
                  key={d.date}
                  className={`cycle-x-label${d.is_today ? ' cycle-x-label-today' : ''}`}
                >
                  {showLabel ? shortMonthDay(d.date) : ''}
                </span>
              )
            })}
          </div>
        </div>
      </div>
      <div className="cycle-axis-titles" aria-hidden="true">
        <span className="cycle-axis-y-title">Predicted pain (0–10)</span>
        <span className="cycle-axis-x-title">Date</span>
      </div>
    </figure>
  )
}
